import { Box, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import shaka from 'shaka-player';
import { formatTime } from '../../helpers';
import { usePlayerContext } from './PlayerContext';

const format = (value: string | number, unit: string) => `${value} (${unit})`;
const parsePx = (value: string | number) => format(value, 'px');
const parsePercent = (value: string | number) => format(value, '%');
const parseFrames = (value: string | number) => format(value, 'frames');
const parseSeconds = (value: number) => format(value.toFixed(2), 's');
const parseBits = (value: number) => format(Math.round(value / 1000), 'kbits/s');
const parseTime = (value: number) => format(formatTime(value), 'm');
const parseGaps = (value: string | number) => format(value, 'gaps');
const parseStalls = (value: string | number) => format(value, 'stalls');

const parseStats = (stats: shaka.extern.Stats) => ({
  width: parsePx(stats.width),
  height: parsePx(stats.height),
  completionPercent: parsePercent(stats.completionPercent),
  bufferingTime: parseSeconds(stats.bufferingTime),
  drmTimeSeconds: parseSeconds(stats.drmTimeSeconds),
  licenseTime: parseSeconds(stats.licenseTime),
  liveLatency: parseSeconds(stats.liveLatency),
  loadLatency: parseSeconds(stats.loadLatency),
  manifestTimeSeconds: parseSeconds(stats.manifestTimeSeconds),
  estimatedBandwidth: parseBits(stats.estimatedBandwidth),
  streamBandwidth: parseBits(stats.streamBandwidth),
  maxSegmentDuration: parseTime(stats.maxSegmentDuration),
  pauseTime: parseTime(stats.pauseTime),
  playTime: parseTime(stats.playTime),
  corruptedFrames: parseFrames(stats.corruptedFrames),
  decodedFrames: parseFrames(stats.decodedFrames),
  droppedFrames: parseFrames(stats.droppedFrames),
  stallsDetected: parseStalls(stats.stallsDetected),
  gapsJumped: parseGaps(stats.gapsJumped),
  version: shaka.Player.version,
});

export const Stats: FC = () => {
  const { player } = usePlayerContext();
  const [stats, setStats] = useState<Partial<Record<keyof shaka.extern.Stats, string>>>();

  useEffect(() => {
    const getPlayerStats = () => {
      const playerStats = player?.getStats();
      if (playerStats) setStats(parseStats(playerStats));
    };
    const interval = setInterval(getPlayerStats, 1000);
    return () => clearInterval(interval);
  }, [player]);

  if (!stats) return null;

  return (
    <Box
      sx={({ palette }) => ({
        display: 'flex',
        flexDirection: 'row',
        gap: '16px',
        position: 'absolute',
        top: '16px',
        left: '16px',
        zIndex: '1',
        color: palette.common.white,
        backgroundColor: palette.primary.main,
        opacity: 0.7,
        padding: '8px',
        borderRadius: '8px',
        maxHeight: 'calc(100% - 32px)',
        overflow: 'hidden',
      })}
    >
      <span>
        {Object.keys(stats).map((key, i) => (
          <Typography key={`key_${i}`} variant="p11" sx={{ textTransform: 'capitalize', lineHeight: '16px' }}>
            {key}
          </Typography>
        ))}
      </span>
      <span>
        {Object.values(stats).map((value, i) => (
          <Typography key={`value_${i}`} variant="p11Medium" sx={{ fontFeatureSettings: `'tnum'`, lineHeight: '16px' }}>
            {value}
          </Typography>
        ))}
      </span>
    </Box>
  );
};
